// Eskom PARCC — in-app notification centre bell (Lindiwe feedback Jun 23 2026, 4a).
//
// A header bell with an unread badge; clicking opens a dropdown of the signed-in
// user's own notifications (QA notes, review completions, revision requests,
// allocations). Clicking a notification marks it read and jumps to the related
// transaction / working paper. Reads + mark-read self-scope at the connector, so a
// user only ever sees and touches their own.
import { useState } from 'react';
import { Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useMyNotifications, useMarkNotificationRead, useMarkAllNotificationsRead } from '../../hooks';
import type { NotificationRead } from '../../lib/db';

export function NotificationBell(): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { data: notifications = [] } = useMyNotifications();
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();
  const unread = notifications.filter((n) => !n.read).length;

  function openEntity(n: NotificationRead): void {
    if (!n.read) markRead.mutate(n.id);
    if (n.entityId && n.entityType === 'CesRun') {
      // An AI review finished — land on the CES sheet, where the proposals are.
      navigate(`/ces/${encodeURIComponent(n.entityId)}`);
    } else if (n.entityId && n.entityType === 'Transaction') {
      navigate(`/transactions?ref=${encodeURIComponent(n.entityId)}`);
    } else if (n.entityType === 'WorkingPaper') {
      navigate('/working-papers');
    }
    setOpen(false);
  }

  return (
    <div className="notif">
      <button
        type="button"
        className="btn ghost icon-only notif-bell"
        aria-label={unread > 0 ? `Notifications (${unread} unread)` : 'Notifications'}
        aria-haspopup="true"
        aria-expanded={open}
        title="Notifications"
        onClick={() => setOpen((o) => !o)}
      >
        <Bell size={16} aria-hidden />
        {unread > 0 && (
          <span className="notif-badge" aria-hidden>
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="notif-backdrop" onClick={() => setOpen(false)} aria-hidden />
          <div className="notif-panel" role="menu" aria-label="Notifications">
            <div className="notif-head">
              <strong>Notifications</strong>
              {unread > 0 && (
                <button type="button" className="btn ghost sm" onClick={() => markAll.mutate()}>
                  Mark all read
                </button>
              )}
            </div>
            {notifications.length === 0 ? (
              <div className="notif-empty small">You have no notifications.</div>
            ) : (
              <ul className="notif-list">
                {notifications.slice(0, 50).map((n) => (
                  <li key={n.id} className={`notif-item${n.read ? '' : ' unread'}`}>
                    <button type="button" className="notif-item-btn" onClick={() => openEntity(n)}>
                      <span className="notif-title">{n.title}</span>
                      {n.body && <span className="notif-body">{n.body}</span>}
                      <span className="notif-time small">{new Date(n.createdAt).toLocaleString('en-ZA')}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}
